import axios from 'axios'
import {
  SessionDataArgs,
  WorkflowChannel,
} from '../graphQL/queries/getSession/types'
import { WHATSAPP_ME_URL } from '../consts/defaultConfigValues/defaultConstants'
import getGoToChannelUrl from '../utils/common/getGoToChannelUrl'

interface ICreateOrderService {
  data: SessionDataArgs
  phoneNumber: string
  workflowApi: string
  botrunnerApi: string
}

/**
 * Where the shopper is sent once the flow has been signalled, and by which of
 * the two variants.
 *
 * Returned rather than acted on, so that suppressing the redirect is the
 * caller's decision. `ng` is kept because the two variants do not build the
 * same URL — one goes through `getGoToChannelUrl`, the other concatenates
 * `WHATSAPP_ME_URL` — and collapsing them would change where every legacy
 * storefront lands.
 * @internal
 */
export interface IStephookOutcome {
  ng: boolean
  channel: `${WorkflowChannel}`
  phoneNumber: string
}

/**
 * Tells the conversation flow to advance, and returns where the shopper would
 * go next.
 *
 * **This is the POST, and only the POST.** It was extracted from
 * `postCreateOrder` because the in-webview confirmation flow needs the same
 * signal without the navigation that used to follow it: for an opted-in
 * storefront the order already exists and the shopper stays on the
 * confirmation screen until they choose to leave (M1-B Jot Down D-10, D-4).
 * The alternative — a boolean threaded through `postCreateOrder` — would put
 * the decision inside the function every storefront's checkout depends on,
 * where a default flip is silent.
 *
 * Two variants, chosen by `configuration.workflow[name].ng`, and the requests
 * are unchanged from before the split: the workflow webhook takes
 * `{ userId }`, the botrunner takes `{ userId, state }`. A non-200 throws, as
 * it did.
 * @internal
 */
export const postStephook = async (
  params: ICreateOrderService,
): Promise<IStephookOutcome> => {
  const { data, phoneNumber, workflowApi, botrunnerApi } = params
  const { workflow, configuration } = data
  const currentWorkflowConfig =
    configuration?.workflow &&
    workflow?.name &&
    configuration.workflow[workflow.name]
  if (!currentWorkflowConfig) throw new Error('Unexpected error')
  let ngUrl = ''
  const stephookValue =
    currentWorkflowConfig.stephook && currentWorkflowConfig.stephook.preorder
      ? currentWorkflowConfig.stephook.preorder
      : ''
  const phone = currentWorkflowConfig.channelUid || phoneNumber
  const channel = currentWorkflowConfig.channel || WorkflowChannel.WHATSAPP
  if (currentWorkflowConfig.ng && currentWorkflowConfig.ng === true) {
    ngUrl = `${workflowApi}${stephookValue}/webhook?token=${currentWorkflowConfig.jwtToken}`
    const res = await axios.post(ngUrl, { userId: workflow.userUid })
    if (res.status !== 200) {
      throw new Error('Unexpected error')
    }
    return { ng: true, channel, phoneNumber: phone }
  }
  ngUrl = currentWorkflowConfig
    ? `${botrunnerApi}${workflow.channel}/sent-to?jwt=${currentWorkflowConfig.jwtToken}`
    : ''
  const res = await axios.post(ngUrl, {
    userId: workflow?.userUid,
    state: stephookValue,
  })
  if (res.status !== 200) {
    throw new Error('Unexpected error')
  }
  return { ng: false, channel, phoneNumber: phone }
}

/**
 * The legacy checkout exit: signal the flow, then hand the shopper back to the
 * conversation.
 *
 * **The default path, and it must stay byte-identical.** Every storefront that
 * has not authored `options.inWebviewOrderConfirmation` reaches checkout
 * through here, the order is created by the flow afterwards, and the redirect
 * is what gets the shopper to the conversation where that happens. Both
 * variants assign to `window.location` exactly as they did before the POST was
 * split out — `.href` with the channel URL on the `ng` path, the whole
 * `location` with a bare `wa.me` URL on the botrunner path.
 */
const postCreateOrder = async (params: ICreateOrderService) => {
  const { ng, channel, phoneNumber } = await postStephook(params)
  if (ng) {
    const goToChannelUrl = getGoToChannelUrl({
      channel,
      phoneNumber,
    })
    window.location.href = goToChannelUrl
  } else {
    window.location = `${WHATSAPP_ME_URL}${phoneNumber}` as any
  }
}

export default postCreateOrder
